import { useState, useCallback, useMemo } from 'react';

/**
 * @fileoverview Hook de UI para manejo de estado del sidebar derecho del inbox
 * @module useInboxRightSidebarUI
 * @category UI Layer
 * @since 1.0.0
 * @version 2.1.0
 */

/**
 * @typedef {Object} UseInboxRightSidebarUIReturn
 * @description Objeto retornado por el hook con estado de UI y funciones de control
 * @property {string} activeTab - Tab actualmente activo en el sidebar
 * @property {function(string): void} setActiveTab - Función directa para cambiar el tab activo
 * @property {function(string): void} handleTabChange - Handler optimizado para cambio de tab con validaciones
 * @property {function(): void} resetUIState - Función para resetear estado de UI al valor por defecto
 */

/**
 * Hook de UI para InboxRightSidebarMC - Manejo de estado de tabs y controles visuales.
 * 
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades,
 * enfocándose únicamente en el estado de interfaz de usuario del sidebar derecho
 * del inbox sin manejar lógica de negocio ni llamadas API.
 * 
 * @function useInboxRightSidebarUI
 * @category Hook - UI Layer
 * 
 * @description
 * **Arquitectura Optimizada (v2.1.0):**
 * - ✅ **UI State Management**: Control completo de estado de tabs
 * - ✅ **Performance**: useCallback y useMemo para optimización completa
 * - ✅ **Validation**: Validación de cambios de tab para evitar re-renders innecesarios
 * - ✅ **Reset Functionality**: Capacidad de resetear estado al valor inicial
 * 
 * **Responsabilidades:**
 * - Manejar estado de tab activo en el sidebar
 * - Controlar transiciones entre tabs con validaciones
 * - Proporcionar funcionalidad de reset de estado
 * - Optimizar re-renders con memoización estratégica
 * - Soporte de debugging en desarrollo
 * 
 * **NO Maneja:**
 * - ❌ Llamadas a API o carga de datos → useInboxRightSidebarData
 * - ❌ Lógica de negocio → useInboxRightSidebarLogic
 * - ❌ Procesamiento de datos → Forms Layer hooks
 * - ❌ Estado de modales globales → useInboxUI
 * - ❌ Gestión de eventos de usuario complejos → Components
 * 
 * **Tabs Soportados:**
 * - `customer-info`: Información general del cliente (por defecto)
 * - `custom-fields`: Campos personalizados del cliente
 * - `deals`: Deals y oportunidades del cliente
 * - `history`: Historial de interacciones
 * - `notes`: Notas del cliente
 * 
 * @param {string} [defaultActiveTab='customer-info'] - Tab activo por defecto al inicializar
 * 
 * @returns {UseInboxRightSidebarUIReturn} Objeto con estado de UI y funciones de control
 * 
 * @example
 * ```jsx
 * // Uso básico en componente sidebar
 * import useInboxRightSidebarUI from 'hooks/inbox/ui/useInboxRightSidebarUI';
 * 
 * const InboxRightSidebarMC = () => {
 *   const { 
 *     activeTab, 
 *     handleTabChange,
 *     resetUIState
 *   } = useInboxRightSidebarUI('customer-info');
 * 
 *   return (
 *     <div className="tw-wrapper">
 *       <nav className="tw-border-b">
 *         <button 
 *           className={`tw-tab ${activeTab === 'customer-info' ? 'tw-active' : ''}`}
 *           onClick={() => handleTabChange('customer-info')}
 *         >
 *           Cliente
 *         </button>
 *         <button 
 *           className={`tw-tab ${activeTab === 'custom-fields' ? 'tw-active' : ''}`}
 *           onClick={() => handleTabChange('custom-fields')}
 *         >
 *           Campos
 *         </button>
 *       </nav>
 *       
 *       {activeTab === 'customer-info' && <CustomerInfoTab />}
 *       {activeTab === 'custom-fields' && <CustomFieldsTab />}
 *     </div>
 *   );
 * };
 * ```
 * 
 * @example
 * ```jsx
 * // Integración con tab dinámico basado en datos
 * const DynamicSidebar = ({ customerData }) => {
 *   const initialTab = customerData?.hasCustomFields ? 'custom-fields' : 'customer-info';
 *   const { activeTab, handleTabChange, resetUIState } = useInboxRightSidebarUI(initialTab);
 * 
 *   // Reset cuando cambia el cliente
 *   useEffect(() => {
 *     if (customerData?.id) {
 *       resetUIState();
 *     }
 *   }, [customerData?.id, resetUIState]);
 * 
 *   const availableTabs = useMemo(() => [
 *     { id: 'customer-info', label: 'Cliente', visible: true },
 *     { id: 'custom-fields', label: 'Campos', visible: customerData?.hasCustomFields },
 *     { id: 'deals', label: 'Deals', visible: customerData?.dealsCount > 0 },
 *     { id: 'history', label: 'Historial', visible: true }
 *   ], [customerData]);
 * 
 *   return (
 *     <TabContainer 
 *       activeTab={activeTab}
 *       tabs={availableTabs}
 *       onTabChange={handleTabChange}
 *     />
 *   );
 * };
 * ```
 * 
 * @example
 * ```jsx
 * // Control avanzado con validaciones personalizadas
 * const AdvancedSidebar = () => {
 *   const { activeTab, handleTabChange, resetUIState } = useInboxRightSidebarUI();
 * 
 *   const handleTabChangeWithValidation = useCallback((newTab) => {
 *     // Validación personalizada antes del cambio
 *     if (newTab === 'deals' && !customerHasPermission('view_deals')) {
 *       showErrorToast('No tienes permisos para ver deals');
 *       return;
 *     }
 * 
 *     // Confirmar cambio si hay datos no guardados
 *     if (hasUnsavedChanges && activeTab === 'custom-fields') {
 *       confirmTabChange(newTab, handleTabChange);
 *     } else {
 *       handleTabChange(newTab);
 *     }
 *   }, [activeTab, handleTabChange]);
 * 
 *   return (
 *     <TabInterface 
 *       currentTab={activeTab}
 *       onTabChange={handleTabChangeWithValidation}
 *       onReset={resetUIState}
 *     />
 *   );
 * };
 * ```
 * 
 * @see {@link useInboxRightSidebarData} Para carga de datos del sidebar
 * @see {@link useInboxUI} Para estado global de UI del inbox
 * @see {@link useCustomerDetailLogic} Para procesamiento de datos de cliente
 * 
 * @since 1.0.0
 * @version 2.1.0 - Optimización completa, debugging support, tipado TypeScript-style
 */
function useInboxRightSidebarUI(defaultActiveTab = 'customer-info') {
  /**
   * Estado del tab activo en el sidebar derecho.
   * 
   * @type {string}
   * @description
   * Mantiene el identificador del tab actualmente visible en el sidebar.
   * Se inicializa con defaultActiveTab y puede cambiar a través de las
   * funciones de control proporcionadas por el hook.
   * 
   * **Valores posibles:**
   * - 'customer-info': Información general del cliente
   * - 'custom-fields': Campos personalizados del cliente  
   * - 'deals': Deals y oportunidades
   * - 'history': Historial de interacciones
   * - 'notes': Notas del cliente
   * 
   * @since 1.0.0
   */
  const [activeTab, setActiveTab] = useState(defaultActiveTab);

  /**
   * Handler optimizado para cambio de tab con validación de estado.
   * 
   * @function handleTabChange
   * @category Event Handler
   * @description
   * Función memoizada que maneja el cambio de tab activo con validación
   * para evitar cambios innecesarios y re-renders. Solo actualiza el estado
   * si el nuevo tab es diferente al actual.
   * 
   * **Optimizaciones:**
   * - Validación de cambio para evitar updates innecesarios
   * - Memoizada con useCallback para performance
   * - Dependencias específicas para re-creación mínima
   * 
   * @param {string} newTab - Identificador del nuevo tab a activar
   * @param {string} newTab.id - Debe ser uno de los tabs válidos del sidebar
   * 
   * @returns {void}
   * 
   * @example
   * ```javascript
   * // Uso básico
   * handleTabChange('custom-fields');
   * 
   * // Con validación en componente
   * const handleTabClick = (tabId) => {
   *   if (isValidTab(tabId)) {
   *     handleTabChange(tabId);
   *   }
   * };
   * 
   * // En event handler
   * <button onClick={() => handleTabChange('deals')}>
   *   Ver Deals
   * </button>
   * ```
   * 
   * @performance O(1) - Comparación simple de strings
   * @memoized Depende de activeTab
   * @since 1.0.0
   */
  const handleTabChange = useCallback((newTab) => {
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [activeTab]);

  /**
   * Función para resetear el estado de UI del sidebar al valor inicial.
   * 
   * @function resetUIState
   * @category State Management
   * @description
   * Función memoizada que restaura el tab activo al valor por defecto
   * especificado en defaultActiveTab. Útil para limpiar el estado cuando
   * cambia el contexto (ej: nueva conversación, nuevo cliente).
   * 
   * **Casos de uso:**
   * - Cambio de conversación seleccionada
   * - Cambio de cliente en el sidebar
   * - Reset manual del estado de UI
   * - Navegación a nuevo contexto
   * - Cleanup al desmontar componente
   * 
   * **Optimización:** Memoizada con useCallback para evitar re-creación
   * innecesaria en re-renders del componente padre.
   * 
   * @returns {void}
   * 
   * @example
   * ```javascript
   * // Reset básico
   * resetUIState();
   * 
   * // En efecto de cambio de conversación
   * useEffect(() => {
   *   if (conversationId) {
   *     resetUIState(); // Volver al tab por defecto
   *   }
   * }, [conversationId, resetUIState]);
   * 
   * // En handler de navegación
   * const handleNewCustomer = (customerId) => {
   *   loadCustomer(customerId);
   *   resetUIState(); // Limpiar tab anterior
   * };
   * 
   * // En cleanup de componente
   * useEffect(() => {
   *   return () => {
   *     resetUIState(); // Cleanup al desmontar
   *   };
   * }, [resetUIState]);
   * ```
   * 
   * @performance O(1) - Operación de setState simple
   * @memoized Depende de defaultActiveTab
   * @since 1.0.0
   */
  const resetUIState = useCallback(() => {
    setActiveTab(defaultActiveTab);
  }, [defaultActiveTab]);

  /**
   * Objeto retornado memoizado para evitar re-renders innecesarios de componentes.
   * 
   * @type {UseInboxRightSidebarUIReturn}
   * @description
   * Retorna todas las funciones y estado de UI del hook en un objeto
   * memoizado que solo cambia cuando las dependencias realmente cambian,
   * optimizando el rendimiento de componentes que usan este hook.
   * 
   * **Estado incluido:**
   * - `activeTab`: String con el identificador del tab activo actual
   * 
   * **Funciones incluidas:**
   * - `setActiveTab`: Setter directo para casos de uso avanzados
   * - `handleTabChange`: Handler optimizado con validaciones
   * - `resetUIState`: Función para reset a estado inicial
   * 
   * **Patrón de uso recomendado:**
   * Usar `handleTabChange` para la mayoría de casos de uso ya que incluye
   * validaciones y optimizaciones. Reservar `setActiveTab` para casos
   * especiales donde se necesite control directo del estado.
   * 
   * @performance Optimizado con useMemo para evitar re-creación de objeto
   * @memoized Depende de activeTab, handleTabChange, resetUIState
   * @since 1.0.0
   */
  return useMemo(() => ({
    activeTab,
    setActiveTab,
    handleTabChange,
    resetUIState
  }), [activeTab, handleTabChange, resetUIState]);
}

export default useInboxRightSidebarUI;